import { systemClock, type Clock } from "./clock";

/**
 * The send arbiter (§10.1, one voice per customer).
 *
 * "All outbound intents pass through a per-contact arbiter: active conversation
 * beats scheduled plays, per-person beats segment-wide, frequency caps and
 * suppression enforced globally, not per-agent. The customer experiences ONE
 * attentive shop, never three enthusiastic robots taking turns."
 *
 * Two things moved here from the agents package on 2026-09-07.
 *
 * It lives in core because the other half of the send path, the window gate,
 * already does, and because the durable store below has to be implemented in
 * `packages/db`, which cannot depend on `packages/agents`.
 *
 * And the state moved OUT of the object. "Enforced globally" was true of
 * agents and false of processes: the counters were a Map in one worker, so two
 * workers each admitted a full day's allowance and the cap was not the cap.
 * That is the defect the budget had before B12's rewrite, in the component
 * that decides how often a real customer hears from this shop.
 */
export type SendPriority = "active_conversation" | "per_person_play" | "segment_wave";

export const PRIORITY_RANK: Record<SendPriority, number> = {
  active_conversation: 3,
  per_person_play: 2,
  segment_wave: 1,
};

export interface SendIntentRequest {
  readonly clientId: string;
  readonly contactId: string;
  readonly agentId: string;
  readonly agentVersion: string;
  readonly threadId: string | null;
  readonly priority: SendPriority;
}

export interface ArbiterDecision {
  readonly allowed: boolean;
  readonly reason: string;
}

/**
 * One deliberate no-send, as it sits on the ledger.
 *
 * B3, verbatim: "Every deliberate no-send is logged as a decision with its
 * reason and enters credit assignment like any send. Models carry
 * unnecessary-action bias; a system that never records restraint can never
 * learn it." F-21 rules that these are ledger rows and not an in-memory array.
 */
export interface RestraintRecord {
  readonly clientId: string;
  readonly contactId: string;
  readonly threadId: string | null;
  readonly agentId: string;
  readonly agentVersion: string;
  /** Which gate withheld the send. `arbiter`, `window`, `verifier`, and so on. */
  readonly gate: string;
  readonly reason: string;
  readonly at: Date;
  /** §5: every event write is idempotent. Four retries leave one decision. */
  readonly idempotencyKey: string;
}

export interface AdmissionRequest {
  readonly clientId: string;
  readonly contactId: string;
  /** `YYYY-MM-DD` in the shop's timezone (`SendArbiter#timeZone`). The day the cap is counted over. */
  readonly day: string;
  readonly priority: SendPriority;
  readonly maxPerDay: number;
  /**
   * Whether this send spends one of the day's `maxPerDay` slots. False is a
   * reply inside a live conversation (`countsTowardCap` below, Kabir's typed
   * 2026-10-16 go): admitted past the cap, spending nothing, but still
   * recorded as the day's top priority so a wave behind it is outranked.
   * The store applies the flag; the policy lives in `SendArbiter`.
   */
  readonly countsTowardCap: boolean;
}

/**
 * Which sends the per-contact daily cap governs.
 *
 * Kabir, typed 2026-10-16: "ok well do what is needde and ignore what isnt",
 * in reply to a recommendation list whose item 2 read: "C1 cap, T3, yours.
 * Rec: day counted in the shop's timezone, and a reply inside an active
 * window exempt from the per-day cap, which then governs proactive sends
 * only. The cap exists to stop robots, not conversations." The evidence was
 * the 2026-10-16 funnel audit: the cap bound on 5 to 14 percent of active
 * thread-days in the shop's corpus, refusing a buyer's fourth reply in one
 * back-and-forth with `frequency_cap`, and the UTC day reset at 8pm Eastern.
 *
 * An `active_conversation` intent reaches the arbiter only through
 * `evaluateSendPath`, after the window gate, so the exemption is for a reply
 * inside the customer's own 24h window and nothing else. Every proactive
 * play (`per_person_play`, `segment_wave`) still counts, and the number (3,
 * TA-81) is unchanged.
 */
export function countsTowardCap(priority: SendPriority): boolean {
  return priority !== "active_conversation";
}

export type AdmissionOutcome =
  | { readonly admitted: true; readonly countToday: number }
  | { readonly admitted: false; readonly reason: "frequency_cap"; readonly countToday: number }
  | { readonly admitted: false; readonly reason: "outranked"; readonly by: SendPriority };

/**
 * Where the arbiter's state actually lives.
 *
 * `admit` must be ONE atomic operation, for the reason the budget store gives
 * at length: a read followed by a write has a window, and two workers polling
 * the same queue will eventually hit it. A refusal must not consume a slot,
 * so a blocked wave cannot spend the allowance of the conversation it lost to.
 */
export interface ArbiterStore {
  /**
   * False means a restart takes the day's cap and suppression list with it,
   * and two workers each hold their own private cap, which is not the cap
   * §10.1 requires. The worker and API banners read this (via
   * `SendArbiter#durable`) so a deployment cannot be quietly wrong about it.
   */
  readonly durable: boolean;
  admit(req: AdmissionRequest): Promise<AdmissionOutcome>;
  suppress(clientId: string, contactId: string, reason: string, at: Date): Promise<void>;
  suppressionOf(clientId: string, contactId: string): Promise<string | null>;
  recordRestraint(r: RestraintRecord): Promise<void>;
  restraintLog(clientId: string, contactId: string): Promise<readonly RestraintRecord[]>;
}

/** The process-local store. Correct for tests and a single-process demo, and
 * nothing else: it is exactly the shape §10.1's "globally" rules out. */
export class InMemoryArbiterStore implements ArbiterStore {
  readonly durable = false;
  readonly #days = new Map<string, { count: number; top: SendPriority }>();
  readonly #suppressed = new Map<string, string>();
  readonly #restraint = new Map<string, RestraintRecord>();

  clear(): void {
    this.#days.clear();
    this.#suppressed.clear();
    this.#restraint.clear();
  }

  async admit(req: AdmissionRequest): Promise<AdmissionOutcome> {
    const key = `${req.clientId}:${req.contactId}:${req.day}`;
    const spend = req.countsTowardCap ? 1 : 0;
    const state = this.#days.get(key);
    if (!state) {
      if (req.countsTowardCap && req.maxPerDay < 1) {
        return { admitted: false, reason: "frequency_cap", countToday: 0 };
      }
      this.#days.set(key, { count: spend, top: req.priority });
      return { admitted: true, countToday: spend };
    }
    if (req.countsTowardCap && state.count >= req.maxPerDay) {
      return { admitted: false, reason: "frequency_cap", countToday: state.count };
    }
    if (PRIORITY_RANK[state.top] > PRIORITY_RANK[req.priority]) {
      return { admitted: false, reason: "outranked", by: state.top };
    }
    this.#days.set(key, { count: state.count + spend, top: req.priority });
    return { admitted: true, countToday: state.count + spend };
  }

  async suppress(clientId: string, contactId: string, reason: string): Promise<void> {
    this.#suppressed.set(`${clientId}:${contactId}`, reason);
  }

  async suppressionOf(clientId: string, contactId: string): Promise<string | null> {
    return this.#suppressed.get(`${clientId}:${contactId}`) ?? null;
  }

  async recordRestraint(r: RestraintRecord): Promise<void> {
    // Keyed, not pushed: the same decision written twice is one decision.
    const key = `${r.clientId}:${r.idempotencyKey}`;
    if (!this.#restraint.has(key)) this.#restraint.set(key, r);
  }

  async restraintLog(clientId: string, contactId: string): Promise<readonly RestraintRecord[]> {
    return [...this.#restraint.values()].filter(
      (r) => r.clientId === clientId && r.contactId === contactId,
    );
  }
}

export interface ArbiterOptions {
  readonly clock?: Clock;
  readonly maxPerContactPerDay: number;
  readonly store?: ArbiterStore;
  /**
   * The shop's IANA timezone (`clients.timezone`), the zone the cap's day is
   * counted in. Kabir, typed 2026-10-16 (see `countsTowardCap`): a UTC day
   * hands a New York shop's contact a fresh allowance at 8pm Eastern. Absent
   * means UTC, which the runtime only passes when the shop has no zone set,
   * and says so on its banner. An unknown zone throws at construction.
   */
  readonly timeZone?: string;
}

/** `YYYY-MM-DD` of `at` in `timeZone`. `en-CA` is the locale whose date format is ISO. */
export function dayInZone(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
}

export class SendArbiter {
  readonly #clock: Clock;
  readonly #store: ArbiterStore;
  /** The zone the day is counted in. */
  readonly timeZone: string;

  constructor(private readonly opts: ArbiterOptions) {
    this.#clock = opts.clock ?? systemClock;
    this.#store = opts.store ?? new InMemoryArbiterStore();
    this.timeZone = opts.timeZone ?? "UTC";
    try {
      dayInZone(this.#clock.now(), this.timeZone);
    } catch {
      throw new Error(`SendArbiter: unknown timezone "${this.timeZone}"; the cap's day has no zone to be counted in`);
    }
  }

  /**
   * Whether the cap this arbiter enforces is the real one.
   *
   * The worker banner prints it beside the budget's, and for the same reason:
   * a process-local cap looks identical to a global one right up to the moment
   * a second worker doubles it (section 0). Derived from the store, the same
   * way `Gateway#budgetDurable` is derived from `BudgetStore#durable`: a flag
   * set once at construction and never read from the thing it describes is a
   * banner that can drift from the store actually wired in behind it.
   */
  get durable(): boolean {
    return this.#store.durable;
  }

  /** Sensitive life changes, opt-outs, bereavement. Beats everything. */
  async suppress(clientId: string, contactId: string, reason: string): Promise<void> {
    await this.#store.suppress(clientId, contactId, reason, this.#clock.now());
  }

  async restraintLog(clientId: string, contactId: string): Promise<readonly RestraintRecord[]> {
    return this.#store.restraintLog(clientId, contactId);
  }

  async recordRestraint(e: Omit<RestraintRecord, "at">): Promise<void> {
    await this.#store.recordRestraint({ ...e, at: this.#clock.now() });
  }

  async admit(intent: SendIntentRequest): Promise<ArbiterDecision> {
    const now = this.#clock.now();
    const day = dayInZone(now, this.timeZone);

    const suppressedReason = await this.#store.suppressionOf(intent.clientId, intent.contactId);
    if (suppressedReason !== null) {
      return this.#deny(intent, day, "suppressed", `suppressed: ${suppressedReason}`);
    }

    const outcome = await this.#store.admit({
      clientId: intent.clientId,
      contactId: intent.contactId,
      day,
      priority: intent.priority,
      maxPerDay: this.opts.maxPerContactPerDay,
      countsTowardCap: countsTowardCap(intent.priority),
    });
    if (outcome.admitted) return { allowed: true, reason: "admitted" };

    if (outcome.reason === "frequency_cap") {
      return this.#deny(
        intent,
        day,
        "frequency_cap",
        `frequency_cap: ${outcome.countToday} sends already today`,
      );
    }
    const code = `outranked_by_${outcome.by}`;
    return this.#deny(intent, day, code, `${code}: a higher-priority play holds this contact`);
  }

  async #deny(
    intent: SendIntentRequest,
    day: string,
    reason: string,
    detail: string,
  ): Promise<ArbiterDecision> {
    // A blocked intent is logged like any other restraint. Sends that vanish
    // without explanation are indistinguishable from bugs.
    //
    // The key is the decision, not the moment: the same agent refused for the
    // same reason on the same contact on the same day is ONE fact, however
    // many times a retry rediscovers it.
    await this.recordRestraint({
      clientId: intent.clientId,
      contactId: intent.contactId,
      threadId: intent.threadId,
      agentId: intent.agentId,
      agentVersion: intent.agentVersion,
      gate: "arbiter",
      reason: detail,
      idempotencyKey: `withheld:arbiter:${intent.agentId}:${intent.contactId}:${day}:${reason}`,
    });
    return { allowed: false, reason };
  }
}
